import { SapphireCoreV1Factory } from '@arcxgame/contracts/src/typings'
import { useRollbar } from '@rollbar/react'
import { Box, Button, Checkbox, Link, Text } from 'components'
import { useAnalytics, useCreditContext } from 'contexts'
import { CoreCapsuleWithAccount } from 'entities'
import { BigNumber, ContractTransaction } from 'ethers'
import { useArcxApi } from 'hooks'
import { useState } from 'react'
import { BorrowAction } from 'types'
import { formatBigNumber, getSpacing } from 'utils'
import { MaxValues } from '.'
import { ACTION, ACTION_TYPES } from './borrowActions'
import { waitForContractValueToChange } from './helpers/waitForContractValueToChange'

export interface ISubmitArea {
  closeModal: () => void
  setIsExitTransaction: (isExitTransaction: boolean) => void
  isExitTransaction: boolean
  coreCapsule: CoreCapsuleWithAccount
  loadingMaxValues: boolean
  hasRiskPrompt: boolean
  hasExitPrompt: boolean
  action: ACTION
  futureLTV: BigNumber
  maxValues?: MaxValues
  input?: BigNumber
}

export const SubmitArea = ({
  closeModal,
  setIsExitTransaction,
  isExitTransaction,
  coreCapsule,
  loadingMaxValues,
  action,
  hasRiskPrompt,
  hasExitPrompt,
  maxValues,
  input,
  futureLTV,
}: ISubmitArea) => {
  const rollbar = useRollbar()
  const { poolCapsules, refreshAllCapsules } = useCreditContext()
  const arcxApi = useArcxApi()
  const [hasApprovedRisk, setHasApprovedRisk] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { track } = useAnalytics()

  const poolCapsule = poolCapsules.get(coreCapsule.borrowPoolAddress)
  const borrowAsset = poolCapsule.supportedDepositAssets[0] // TODO: support multiple stablecoins

  const {
    accountProperties: { maxLTV },
  } = coreCapsule

  const onSubmitClicked = async () => {
    let tx: ContractTransaction
    const optimizedValue = Number(formatBigNumber(futureLTV)) / Number(formatBigNumber(maxLTV))
    const isOptimized = optimizedValue >= 0.59 && optimizedValue <= 0.61

    setIsSubmitting(true)

    try {
      const coreContract = SapphireCoreV1Factory.connect(
        coreCapsule.contractAddress,
        coreCapsule.provider,
      )
      const initialBorrowIndex = await coreContract.borrowIndex()
      const pendingActions: BorrowAction[] = []

      if (isExitTransaction) {
        tx = await coreCapsule.exit(borrowAsset)
        track('Exited', {
          collateralSymbol: coreCapsule.collateralSymbol,
          collateralAmount: formatBigNumber(
            coreCapsule.accountProperties.collateralAmountScaled,
            undefined,
            coreCapsule.collateralDecimals,
          ),
          loanAmount: formatBigNumber(coreCapsule.accountProperties.borrowedAmountScaled),
        })
        pendingActions.push({
          method: 'withdraw',
          amount: input.toString(),
          decimals: coreCapsule.collateralDecimals,
          asset: coreCapsule.collateralSymbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
        pendingActions.push({
          method: 'repay',
          amount: input.toString(),
          decimals: borrowAsset.decimals,
          asset: borrowAsset.symbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
      } else if (action.value === ACTION_TYPES.DEPOSIT) {
        tx = await coreCapsule.deposit(input)
        track('Deposited', {
          amount: formatBigNumber(input, undefined, coreCapsule.collateralDecimals),
          collateral: coreCapsule.collateralSymbol,
        })
        pendingActions.push({
          method: 'deposit',
          amount: input.toString(),
          decimals: coreCapsule.collateralDecimals,
          asset: coreCapsule.collateralSymbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
      } else if (action.value === ACTION_TYPES.BORROW) {
        tx = await coreCapsule.borrow(input, borrowAsset.address)
        track('Borrowed', {
          borrowAsset: borrowAsset.symbol,
          amount: formatBigNumber(input, undefined, borrowAsset.decimals),
          isOptimized: isOptimized.toString(),
        })
        pendingActions.push({
          method: 'borrow',
          amount: input.toString(),
          decimals: borrowAsset.decimals,
          asset: borrowAsset.symbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
      } else if (action.value === ACTION_TYPES.REPAY) {
        tx = await coreCapsule.repay(input, borrowAsset.address)
        track('Repaid', {
          borrowAsset: borrowAsset.symbol,
          amount: formatBigNumber(input, undefined, borrowAsset.decimals),
          isOptimized: isOptimized.toString(),
        })
        pendingActions.push({
          method: 'repay',
          amount: input.toString(),
          decimals: borrowAsset.decimals,
          asset: borrowAsset.symbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
      } else if (action.value === ACTION_TYPES.WITHDRAW) {
        tx = await coreCapsule.withdraw(input)
        track('Withdrew', {
          collateralSymbol: coreCapsule.collateralSymbol,
          amount: formatBigNumber(input, undefined, coreCapsule.collateralDecimals),
        })
        pendingActions.push({
          method: 'withdraw',
          amount: input.toString(),
          decimals: coreCapsule.collateralDecimals,
          asset: coreCapsule.collateralSymbol,
          transactionId: tx.hash,
          timestamp: 0,
          blockNumber: 0,
        })
      } else {
        throw new Error('SubmitArea::onSubmitClicked: Invalid action')
      }

      // Post immediately in case user navigates/refreshes page.
      // If they do leave too soon, the arcx-publisher will fill in the remaining fields.
      await arcxApi.postPendingImpactTx({
        account: coreCapsule.account,
        actions: pendingActions,
      })

      const receiptTx = await tx.wait()
      const timestamp = (await receiptTx.events[0].getBlock()).timestamp

      // Post again to set blockNumber and timestamp.
      // Will update previous call since it has same transactionId
      await arcxApi.postPendingImpactTx({
        account: coreCapsule.account,
        actions: pendingActions.map((action) => ({
          ...action,
          timestamp,
          blockNumber: receiptTx.blockNumber,
          transactionId: receiptTx.transactionHash,
        })),
      })

      // Borrow index should change after tx
      await waitForContractValueToChange(initialBorrowIndex, () => coreContract.borrowIndex())
      await refreshAllCapsules()

      setHasApprovedRisk(false)
      setIsExitTransaction(false)
    } catch (err) {
      // TODO: we should display a UI component to notify the user of the error
      // https://app.asana.com/0/1202613884569678/1202655596084599
      console.error('SubmitArea::onSubmitClicked:', err)
      rollbar.error('SubmitArea::onSubmitClicked:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isButtonDisabled = () => {
    if (!input || input.isZero() || !maxValues) {
      return true
    }

    if (input.gt(maxValues[action.value])) {
      return true
    }

    if (hasRiskPrompt && !hasApprovedRisk && !isExitTransaction) {
      return true
    }

    return isSubmitting || loadingMaxValues
  }

  let errorMsg: JSX.Element | undefined
  if (!coreCapsule.accountCanBorrow && coreCapsule.accountProperties.collateralAmountScaled.eq(0)) {
    errorMsg = (
      <>
        <Text color="YELLOW" size="xs" weight="thin">
          ARCx is in Beta and only available to specific accounts. Join our&nbsp;
        </Text>
        <Link
          color="YELLOW"
          externalURL="https://discord.gg/arcx"
          size="xs"
          weight="thin"
          style={{ textDecoration: 'underline' }}
        >
          Discord
        </Link>
        <Text color="YELLOW" size="xs" weight="thin">
          &nbsp;for more information.
        </Text>
      </>
    )
  }

  if (errorMsg) {
    return (
      <Box flexDirection="column">
        <Box
          textAlign="center"
          display="inline"
          flexDirection="row"
          paddingBottom={getSpacing('l')}
          paddingTop={getSpacing('s')}
        >
          {errorMsg}
        </Box>
        <Box flexDirection="row" justifyContent="space-around">
          <Button onClick={closeModal} text="Cancel" variant="secondary" width="45%" />
          <Button
            loading={isSubmitting}
            disabled={isButtonDisabled()}
            onClick={() => onSubmitClicked()}
            text={isExitTransaction ? 'Exit' : action.label}
            variant="fancy"
            width="45%"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box flexDirection="column">
      {hasExitPrompt && (
        <Box
          flexDirection="row"
          alignItems="center"
          paddingBottom={getSpacing('s')}
          paddingLeft={getSpacing('s')}
        >
          <Checkbox
            checked={isExitTransaction}
            onChange={() => {
              setIsExitTransaction(!isExitTransaction)
            }}
            label="Exit my vault position"
            labelWeight="thin"
            labelSize="s"
          />
        </Box>
      )}
      {!isExitTransaction && hasRiskPrompt && (
        <Box flexDirection="row" paddingBottom={getSpacing('m')}>
          <Box flexDirection="column">
            <Box flexDirection="row" alignItems="center">
              <Text color="YELLOW" size="s" weight="regular">
                This action will increase risk of liquidation.
              </Text>
            </Box>
            <Box
              flexDirection="row"
              alignItems="center"
              paddingTop={getSpacing('s')}
              paddingLeft={getSpacing('s')}
            >
              <Checkbox
                checked={hasApprovedRisk}
                onChange={() => {
                  setHasApprovedRisk(!hasApprovedRisk)
                }}
                label="I acknowledge the risks involved."
                labelWeight="thin"
                labelSize="s"
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* The if they can access but can't borrow, its because their score dropped. */}
      {!coreCapsule.accountCanBorrow && (
        <Box flexDirection="row" paddingBottom={getSpacing('m')}>
          <Text color="YELLOW" size="s" weight="thin" center={true}>
            Borrowing has been disabled in this vault because your DeFi Credit Score is lower than
            the Score Threshold.
          </Text>
        </Box>
      )}

      <Box flexDirection="row" justifyContent="space-around">
        <Button onClick={closeModal} text="Cancel" variant="secondary" width="45%" />
        <Button
          loading={isSubmitting}
          disabled={isButtonDisabled()}
          onClick={() => onSubmitClicked()}
          text={isExitTransaction ? 'Exit' : action.label}
          variant="fancy"
          width="45%"
        />
      </Box>
    </Box>
  )
}
